import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Loader2, ExternalLink } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeachingProgressPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/planning/overview`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } })
      .then((r) => r.json()).then(setData);
  }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="teaching-progress-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BarChart3 className="h-6 w-6 text-emerald-600" />Teaching Progress</h1>
        <p className="text-sm text-slate-500 mt-1">Lessons taught against the planning workbook, per subject and teacher. "Due" = lessons scheduled up to today.</p>
      </div>
      {data.workbooks.length === 0 && <p className="text-slate-500 italic">No planning workbooks uploaded yet.</p>}
      {data.workbooks.map((w) => {
        const expected = w.lessons ? Math.round((100 * w.due_to_date) / w.lessons) : 0;
        return (
          <div key={w.workbook.id} className="rounded-xl border bg-white p-5 space-y-4" data-testid={`progress-workbook-${w.workbook.subject}-${w.workbook.year_level}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="font-semibold text-slate-900">Year {w.workbook.year_level} {w.workbook.subject}</h2>
                <p className="text-xs text-slate-500">{w.workbook.title} · {w.lessons} lessons · {w.due_to_date} due to date ({expected}%)</p>
              </div>
              <Link to={`/dashboard/planner?subject=${encodeURIComponent(w.workbook.subject)}&year=${w.workbook.year_level}&tab=year`} className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1">Open planner <ExternalLink className="h-3 w-3" /></Link>
            </div>
            {w.teachers.length === 0 && <p className="text-sm text-slate-400 italic">No teacher assigned to this subject/year.</p>}
            {w.teachers.map((t) => {
              const behind = expected - t.overall.percent;
              return (
                <div key={t.teacher_id} className="space-y-1" data-testid={`progress-teacher-${t.teacher_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-800">{t.name}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-2">
                      {t.overall.taught}/{t.overall.planned} taught · {t.overall.carried_over} carried
                      <span className="font-semibold text-emerald-700">{t.overall.percent}%</span>
                      {behind > 10 ? <Badge className="bg-rose-100 text-rose-700 border-rose-200" variant="outline">{behind}% behind plan</Badge>
                        : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">On track</Badge>}
                    </span>
                  </div>
                  <Progress value={t.overall.percent} className="h-2" />
                  <div className="flex flex-wrap gap-1 pt-1">
                    {t.by_term.map((term) => <Badge key={term.term} variant="outline" className="text-[11px] bg-slate-50">{term.term}: {term.percent}%</Badge>)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
